import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import Box from "../../components/Box";
import CustomTextInput from "../../components/CustomTextInput";
import { KTCard } from "../../_metronic/helpers/components/KTCard";
import { KTCardBody } from "../../_metronic/helpers/components/KTCardBody";
import Content from "../../_metronic/layout/components/content/Content";
import { ToolbarWrapper } from "../../_metronic/layout/components/toolbar";
import { PageTitle } from "../../_metronic/layout/core";
import { storeActivityIndicator, storeAreYouSureModalCallback } from "../../redux/reducers/authSlice";
import { CalismaGunu, CalismaMolasi, CalismaSaati as CalismaSaatiModel, CalismaSaatiService } from "../../services/CalismaSaatiService";
import {
    saatAraligiCoz, saatAraligiEtiketi, saatAraligiHatasi, saatAraligiMetni, talepAraliklariHatasi, talepAraliklariniDuzenle, VARSAYILAN_TALEP_ARALIKLARI
} from "../../helpers/saatAraligi";

type GunAnahtari = 'haftaIci' | 'cuma' | 'cumartesi' | 'pazar';

/** Mola gün işaretleri: gün satırıyla aynı sırada. */
type MolaGunAlani = 'pazartesiPersembe' | 'cuma' | 'cumartesi' | 'pazar';

const GUNLER: { anahtar: GunAnahtari, ad: string, molaAlani: MolaGunAlani, kisaAd: string }[] = [
    { anahtar: 'haftaIci', ad: 'Pazartesi–Perşembe', molaAlani: 'pazartesiPersembe', kisaAd: 'Pt–Pe' },
    { anahtar: 'cuma', ad: 'Cuma', molaAlani: 'cuma', kisaAd: 'Cuma' },
    { anahtar: 'cumartesi', ad: 'Cumartesi', molaAlani: 'cumartesi', kisaAd: 'Cumartesi' },
    { anahtar: 'pazar', ad: 'Pazar', molaAlani: 'pazar', kisaAd: 'Pazar' },
];

/** Molalar grid satırı (değişiklikler sayfadaki Kaydet'e kadar yereldir). */
interface MolaSatiri extends CalismaMolasi {
    anahtar: string
    isSelected: boolean
}

let anahtarSayaci = 1;
const yeniAnahtar = () => 'mola-' + (anahtarSayaci++);

const bosGun = (calisir: boolean): CalismaGunu => ({ calisir, baslangic: null, bitis: null, araBaslangic: null, araBitis: null, bildirimDk: null, hatirlatmaDk: null });

/** Mesai hatırlatma sınırları (sunucudaki MesaiHatirlatmaHesap ile aynı). */
const BILDIRIM_DK = { enAz: 0, enFazla: 240 };
const HATIRLATMA_DK = { enAz: 1, enFazla: 120 };

/** "HH:mm" → dakika; boşsa null. */
const dakika = (s: string | null | undefined) => {
    if (!s)
        return null;

    var p = s.split(':');
    return Number(p[0]) * 60 + Number(p[1]);
}

/** İki aralık kesişiyor mu (uç uca değmek kesişme sayılmaz). */
const kesisir = (a1: number, a2: number, b1: number, b2: number) => Math.min(a2, b2) > Math.max(a1, b1);

/** Çarpan alanı metni → sayı (virgül de kabul). */
const sayi = (s: string) => s.trim() == '' ? null : Number(s.replace(',', '.'));

const metin = (n: number | null | undefined) => n == null ? '' : String(n);

const zaman = (s: string | null | undefined) => s ? s.substring(0, 5) : '';

/** Molanın günleri: "Pt–Pe, Cuma". */
const molaGunleri = (m: CalismaMolasi) => GUNLER.filter(g => m[g.molaAlani]).map(g => g.kisaAd).join(', ');

/**
 * Molanın günlere göre kontrolü (sunucudaki doğrulamanın aynısı): işaretli ve çalışılan her günde mola çalışma saatinin içinde olmalı,
 * öğle arasıyla ve o günün başka bir molasıyla çakışmamalı. İşaretli ama çalışılmayan gün yok sayılır. Hata metni döner; sorun yoksa undefined.
 */
const molaGunHatasi = (m: CalismaMolasi, digerleri: CalismaMolasi[], gunler: Record<GunAnahtari, CalismaGunu>): string | undefined => {
    var mb = dakika(m.baslangic)!, ms = dakika(m.bitis)!;

    for (var g of GUNLER) {
        var gun = gunler[g.anahtar];

        if (!m[g.molaAlani] || !gun.calisir)
            continue;

        var bas = dakika(gun.baslangic), bit = dakika(gun.bitis);
        var araBas = dakika(gun.araBaslangic), araBit = dakika(gun.araBitis);

        if (bas == null || bit == null)
            continue;

        if (mb < bas || ms > bit)
            return `${m.ad}: ${g.ad} çalışma saatinin (${gun.baslangic}–${gun.bitis}) içinde olmalıdır.`;

        if (araBas != null && araBit != null && kesisir(mb, ms, araBas, araBit))
            return `${m.ad}: ${g.ad} öğle arasıyla (${gun.araBaslangic}–${gun.araBitis}) çakışıyor.`;

        for (var d of digerleri)
            if (d[g.molaAlani] && kesisir(mb, ms, dakika(d.baslangic)!, dakika(d.bitis)!))
                return `${m.ad}: ${g.ad} "${d.ad}" molasıyla çakışıyor.`;
    }

    return undefined;
}

/**
 * Çalışma Saatleri (İnsan Kaynakları > Tanımlamalar, 11.10.2026; Cuma satırı ve molalar 12.10.2026).
 * İşletme için tek kayıt; liste yok. Girilen bitişin üstü ek mesaidir. Molalar alt grid'de; sayfadaki Kaydet ile birlikte kaydedilir.
 */
const CalismaSaati = () => {
    const pageName = "İnsan Kaynakları > Tanımlamalar > Çalışma Saatleri"
    const dispatch = useDispatch();
    const molaRef = useRef<any>(null);

    const [gunler, setGunler] = useState<Record<GunAnahtari, CalismaGunu>>({
        haftaIci: bosGun(true),
        cuma: bosGun(true),
        cumartesi: bosGun(false),
        pazar: bosGun(false),
    });
    const [carpanHaftaIci, setCarpanHaftaIci] = useState('');
    const [carpanCumartesi, setCarpanCumartesi] = useState('');
    const [carpanPazar, setCarpanPazar] = useState('');
    const [carpanResmiTatil, setCarpanResmiTatil] = useState('');
    const [saatlikBolen, setSaatlikBolen] = useState('200');
    const [updatedDate, setUpdatedDate] = useState<string | null | undefined>(undefined);
    const [updatedUser, setUpdatedUser] = useState<string | null | undefined>(undefined);

    // Molalar grid'i
    const [molalar, setMolalar] = useState<MolaSatiri[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [duzenlenenAnahtar, setDuzenlenenAnahtar] = useState<string | undefined>(undefined);
    const [molaAd, setMolaAd] = useState<string | undefined>(undefined);
    const [molaBaslangic, setMolaBaslangic] = useState('');
    const [molaBitis, setMolaBitis] = useState('');
    const [molaGunleriSecim, setMolaGunleriSecim] = useState<Record<MolaGunAlani, boolean>>({ pazartesiPersembe: true, cuma: true, cumartesi: false, pazar: false });

    // Talep saat aralıkları (QR Mesai Faz 2): "HH:mm-HH:mm"; değişiklikler sayfadaki Kaydet'e kadar yereldir.
    const [talepAraliklari, setTalepAraliklari] = useState<string[]>(VARSAYILAN_TALEP_ARALIKLARI);
    const [araliklarVarsayilan, setAraliklarVarsayilan] = useState(false);
    const [yeniAralikBaslangic, setYeniAralikBaslangic] = useState('');
    const [yeniAralikBitis, setYeniAralikBitis] = useState('');

    useEffect(() => {
        getData();
    }, [])

    const doldur = (d: CalismaSaatiModel) => {
        const gun = (g: CalismaGunu | null | undefined, varsayilan: boolean): CalismaGunu => g ? {
            calisir: g.calisir,
            baslangic: zaman(g.baslangic) || null,
            bitis: zaman(g.bitis) || null,
            araBaslangic: zaman(g.araBaslangic) || null,
            araBitis: zaman(g.araBitis) || null,
            bildirimDk: g.bildirimDk ?? null,
            hatirlatmaDk: g.hatirlatmaDk ?? null,
        } : bosGun(varsayilan);

        setGunler({
            haftaIci: gun(d.haftaIci, true),
            // Eski sunucu Cuma göndermezse hafta içi satırı kullanılır.
            cuma: gun(d.cuma ?? d.haftaIci, true),
            cumartesi: gun(d.cumartesi, false),
            pazar: gun(d.pazar, false),
        });
        setCarpanHaftaIci(metin(d.carpanHaftaIci));
        setCarpanCumartesi(metin(d.carpanCumartesi));
        setCarpanPazar(metin(d.carpanPazar));
        setCarpanResmiTatil(metin(d.carpanResmiTatil));
        setSaatlikBolen(metin(d.saatlikBolen ?? 200));
        setMolalar((d.molalar ?? []).map(m => ({
            anahtar: yeniAnahtar(),
            calismaMolasiId: m.calismaMolasiId,
            ad: m.ad,
            baslangic: zaman(m.baslangic),
            bitis: zaman(m.bitis),
            pazartesiPersembe: m.pazartesiPersembe,
            cuma: m.cuma,
            cumartesi: m.cumartesi,
            pazar: m.pazar,
            isSelected: false
        })));
        // Eski sunucu ya da hiç kaydedilmemiş liste: varsayılan üç aralık önerilir (Kaydet ile yazılır).
        var araliklar = talepAraliklariniDuzenle(d.talepAraliklari ?? []);
        setTalepAraliklari(araliklar.length > 0 ? araliklar : VARSAYILAN_TALEP_ARALIKLARI);
        setAraliklarVarsayilan(araliklar.length == 0);
        setYeniAralikBaslangic('');
        setYeniAralikBitis('');
        formuTemizle();
        setIsOpen(false);
        setUpdatedDate(d.updatedDate);
        setUpdatedUser(d.updatedUser);
    }

    const getData = async () => {
        try {
            dispatch(storeActivityIndicator(true));

            var response = await CalismaSaatiService.getir();

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                return;
            }

            if (response.data)
                doldur(response.data);
        } catch (error: any) {
            toast.error(error);
        } finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    const gunuDegistir = (anahtar: GunAnahtari, alan: keyof CalismaGunu, deger: any) => {
        setGunler({ ...gunler, [anahtar]: { ...gunler[anahtar], [alan]: deger } });
    }

    // ---------------------------------------------------------------- Molalar grid'i

    const scrollTo = () => {
        if (!molaRef.current)
            return;

        var headerOffset = 120;
        var elementPosition = molaRef.current.getBoundingClientRect().top;
        var offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
            top: offsetPosition,
            behavior: "smooth"
        });
    }

    const formuTemizle = () => {
        setDuzenlenenAnahtar(undefined);
        setMolaAd(undefined);
        setMolaBaslangic('');
        setMolaBitis('');
        setMolaGunleriSecim({ pazartesiPersembe: true, cuma: true, cumartesi: false, pazar: false });
    }

    const cancelAdding = () => {
        formuTemizle();
        setIsOpen(!isOpen);
        scrollTo();
    }

    /** Tek molanın alan kontrolü (ad, saat, gün seçimi). Hata metni döner; sorun yoksa undefined. */
    const molaAlanHatasi = (m: CalismaMolasi): string | undefined => {
        if (!m.ad)
            return 'Mola adı zorunludur.';

        if (m.ad.length > 100)
            return `${m.ad}: Mola adı en fazla 100 karakter olabilir.`;

        var bas = dakika(m.baslangic), bit = dakika(m.bitis);

        if (bas == null || bit == null)
            return `${m.ad}: Molanın başlangıç ve bitiş saatleri zorunludur.`;

        if (bit <= bas)
            return `${m.ad}: Molanın bitişi başlangıcından sonra olmalıdır.`;

        if (!m.pazartesiPersembe && !m.cuma && !m.cumartesi && !m.pazar)
            return `${m.ad}: Molanın uygulanacağı en az bir günü seçiniz.`;

        return undefined;
    }

    const saveItem = () => {
        var mola: CalismaMolasi = {
            ad: (molaAd ?? '').trim(),
            baslangic: molaBaslangic,
            bitis: molaBitis,
            ...molaGunleriSecim
        };

        var hata = molaAlanHatasi(mola) ?? molaGunHatasi(mola, molalar.filter(x => x.anahtar != duzenlenenAnahtar), gunler);

        if (hata) {
            toast.error(hata);
            return;
        }

        var eski = molalar.find(x => x.anahtar == duzenlenenAnahtar);
        var satir: MolaSatiri = { ...mola, calismaMolasiId: eski?.calismaMolasiId, anahtar: duzenlenenAnahtar ?? yeniAnahtar(), isSelected: false };

        setMolalar(duzenlenenAnahtar
            ? molalar.map(x => x.anahtar == duzenlenenAnahtar ? satir : x)
            : [...molalar, satir]);

        formuTemizle();
        setIsOpen(false);
        scrollTo();
    }

    const setItemSelected = (_anahtar: string) => {
        setMolalar(molalar.map(x => x.anahtar == _anahtar ? { ...x, isSelected: !x.isSelected } : x));
    }

    const selectAll = () => {
        var isAllSelected = molalar.length == molalar.filter(x => x.isSelected).length;

        setMolalar(molalar.map(x => ({ ...x, isSelected: !isAllSelected })));
    }

    const setUpdateItem = () => {
        var o = molalar.filter(x => x.isSelected)[0];

        setDuzenlenenAnahtar(o.anahtar);
        setMolaAd(o.ad);
        setMolaBaslangic(o.baslangic);
        setMolaBitis(o.bitis);
        setMolaGunleriSecim({ pazartesiPersembe: o.pazartesiPersembe, cuma: o.cuma, cumartesi: o.cumartesi, pazar: o.pazar });

        setIsOpen(true);
        scrollTo();
    }

    const deleteItems = async () => {
        setMolalar(molalar.filter(x => !x.isSelected));
    }

    const seciliSayisi = molalar.filter(x => x.isSelected).length;

    // ---------------------------------------------------------------- Talep saat aralıkları

    const aralikEkle = () => {
        var hata = saatAraligiHatasi(yeniAralikBaslangic, yeniAralikBitis);

        if (hata) {
            toast.error(hata);
            return;
        }

        var yeni = saatAraligiMetni(yeniAralikBaslangic, yeniAralikBitis);

        if (talepAraliklari.includes(yeni)) {
            toast.error(`${saatAraligiEtiketi(yeniAralikBaslangic, yeniAralikBitis)} aralığı zaten listede.`);
            return;
        }

        setTalepAraliklari(talepAraliklariniDuzenle([...talepAraliklari, yeni]));
        setYeniAralikBaslangic('');
        setYeniAralikBitis('');
    }

    const aralikSil = (a: string) => {
        if (talepAraliklari.length <= 1) {
            toast.error('En az bir talep saat aralığı kalmalıdır.');
            return;
        }

        setTalepAraliklari(talepAraliklari.filter(x => x != a));
    }

    // ---------------------------------------------------------------- Kaydet

    /** Hata metni döner; sorun yoksa undefined. */
    const dogrula = (): string | undefined => {
        for (var g of GUNLER) {
            var gun = gunler[g.anahtar];

            if (!gun.calisir)
                continue;

            var bas = dakika(gun.baslangic), bit = dakika(gun.bitis);
            var araBas = dakika(gun.araBaslangic), araBit = dakika(gun.araBitis);

            if (bas == null || bit == null)
                return `${g.ad}: Başlangıç ve Bitiş saatleri zorunludur.`;

            if (gun.bildirimDk != null && (gun.bildirimDk < BILDIRIM_DK.enAz || gun.bildirimDk > BILDIRIM_DK.enFazla))
                return `${g.ad}: Bildirim (Dk) ${BILDIRIM_DK.enAz}–${BILDIRIM_DK.enFazla} arasında olmalıdır.`;

            if (gun.hatirlatmaDk != null && (gun.hatirlatmaDk < HATIRLATMA_DK.enAz || gun.hatirlatmaDk > HATIRLATMA_DK.enFazla))
                return `${g.ad}: Hatırlatma (Dk aralık) ${HATIRLATMA_DK.enAz}–${HATIRLATMA_DK.enFazla} arasında olmalıdır.`;

            if (bit <= bas)
                return `${g.ad}: Bitiş saati başlangıçtan sonra olmalıdır.`;

            if ((araBas == null) != (araBit == null))
                return `${g.ad}: Öğle arası başı ve sonu birlikte girilmelidir.`;

            if (araBas != null && araBit != null && (araBit <= araBas || araBas < bas || araBit > bit))
                return `${g.ad}: Öğle arası çalışma saatleri içinde olmalı ve sonu başından sonra gelmelidir.`;
        }

        // Gün saatleri mola eklendikten sonra değişmiş olabilir: molalar yeniden kontrol edilir.
        for (var i = 0; i < molalar.length; i++) {
            var molaHatasi = molaAlanHatasi(molalar[i]) ?? molaGunHatasi(molalar[i], molalar.slice(0, i), gunler);

            if (molaHatasi)
                return molaHatasi;
        }

        var carpanlar: [string, string, boolean][] = [
            ['Hafta içi', carpanHaftaIci, true],
            ['Cumartesi', carpanCumartesi, true],
            ['Pazar', carpanPazar, true],
            ['Resmi tatil', carpanResmiTatil, false],
        ];

        for (var [ad, deger, zorunlu] of carpanlar) {
            var n = sayi(deger);

            if (n == null) {
                if (zorunlu)
                    return `${ad} ek mesai çarpanı zorunludur.`;
                continue;
            }

            if (isNaN(n) || n <= 0)
                return `${ad} ek mesai çarpanı 0'dan büyük olmalıdır.`;
        }

        var bolen = sayi(saatlikBolen);

        if (bolen == null || isNaN(bolen) || !Number.isInteger(bolen) || bolen < 1 || bolen > 400)
            return 'Saatlik ücret böleni 1 ile 400 arasında tam sayı olmalıdır.';

        var aralikHatasi = talepAraliklariHatasi(talepAraliklari);

        if (aralikHatasi)
            return 'Talep saat aralıkları: ' + aralikHatasi;

        return undefined;
    }

    const kaydet = async () => {
        try {
            var hata = dogrula();

            if (hata) {
                toast.error(hata);
                return;
            }

            // Çalışılmayan günde saatler gönderilmez.
            const gonder = (g: CalismaGunu): CalismaGunu => g.calisir ? g : bosGun(false);

            dispatch(storeActivityIndicator(true));

            var response = await CalismaSaatiService.kaydet({
                haftaIci: gonder(gunler.haftaIci),
                cuma: gonder(gunler.cuma),
                cumartesi: gonder(gunler.cumartesi),
                pazar: gonder(gunler.pazar),
                carpanHaftaIci: sayi(carpanHaftaIci) ?? 0,
                carpanCumartesi: sayi(carpanCumartesi) ?? 0,
                carpanPazar: sayi(carpanPazar) ?? 0,
                carpanResmiTatil: sayi(carpanResmiTatil),
                saatlikBolen: sayi(saatlikBolen) ?? 200,
                molalar: molalar.map(m => ({
                    calismaMolasiId: m.calismaMolasiId,
                    ad: m.ad,
                    baslangic: m.baslangic,
                    bitis: m.bitis,
                    pazartesiPersembe: m.pazartesiPersembe,
                    cuma: m.cuma,
                    cumartesi: m.cumartesi,
                    pazar: m.pazar
                })),
                talepAraliklari: talepAraliklariniDuzenle(talepAraliklari),
            });

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                return;
            }

            toast.success("Kaydedildi");

            if (response.data)
                doldur(response.data);
        } catch (error: any) {
            toast.error(error);
        } finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    const saatGirdisi = (anahtar: GunAnahtari, alan: 'baslangic' | 'bitis' | 'araBaslangic' | 'araBitis') => {
        var gun = gunler[anahtar];

        return (
            <input
                type="time"
                className="form-control form-control-solid"
                style={{ background: (!gun.calisir ? '#f1f1f4' : undefined), border: '1px solid #F1F1F4' }}
                disabled={!gun.calisir}
                value={gun[alan] ?? ''}
                onChange={(e) => gunuDegistir(anahtar, alan, e.target.value || null)}
            />
        )
    }

    // Mesai hatırlatma dakikaları (#504): yalnız rakam; boş = null.
    const dakikaGirdisi = (anahtar: GunAnahtari, alan: 'bildirimDk' | 'hatirlatmaDk') => {
        var gun = gunler[anahtar];

        return (
            <input
                type="text"
                inputMode="numeric"
                maxLength={3}
                className="form-control form-control-solid text-end"
                style={{ background: (!gun.calisir ? '#f1f1f4' : undefined), border: '1px solid #F1F1F4', minWidth: 80 }}
                disabled={!gun.calisir}
                placeholder={alan == 'bildirimDk' ? 'Yok' : 'Tek'}
                value={metin(gun[alan])}
                onChange={(e) => {
                    var rakam = e.target.value.replace(/[^0-9]/g, '');
                    gunuDegistir(anahtar, alan, rakam == '' ? null : Number(rakam));
                }}
            />
        )
    }

    const carpanGirdisi = (labelText: string, value: string, setValue: (s: string) => void, isLabelRequired: boolean = true) => (
        <React.Fragment>
            <label className={"form-label " + (isLabelRequired ? 'required' : '')}>{labelText}</label>
            <input
                type="number"
                step="0.05"
                min="0"
                className="form-control form-control-solid"
                style={{ border: '1px solid #F1F1F4' }}
                value={value}
                onChange={(e) => setValue(e.target.value)}
            />
        </React.Fragment>
    )

    return (
        <React.Fragment>
            <ToolbarWrapper filters={undefined} />

            <PageTitle breadcrumbs={[{
                title: pageName.split('>').slice(0, pageName.split('>').length - 1).join(' > ') + ' > ',
                isActive: false,
                path: ''
            }]}>{(pageName.split('>')[pageName.split('>').length - 1])}</PageTitle>

            <Box className="mb-10">
                <Content>
                    <KTCard className="ebbe-card">
                        <KTCardBody>
                            <h4 className="mb-10 text-black">Çalışma Saatleri</h4>

                            <div className='table-responsive'>
                                <table id='kt_table_users' className='table align-middle table-row-dashed dataTable no-footer'>
                                    <thead>
                                        <tr className='text-start text-muted fw-bolder text-uppercase'>
                                            <th>Gün</th>
                                            <th>Çalışma günü</th>
                                            <th>Başlangıç</th>
                                            <th>Bitiş</th>
                                            <th>Öğle arası başı</th>
                                            <th>Öğle arası sonu</th>
                                            <th title="Başlangıçtan önce / bitişten sonra ilk bildirimin dakikası">Bildirim (Dk)</th>
                                            <th title="Bildirimin tekrar aralığı; en fazla 3 bildirim">Hatırlatma (Dk aralık)</th>
                                        </tr>
                                    </thead>
                                    <tbody className='text-gray-600 fw-bold'>
                                        {
                                            GUNLER.map(g =>
                                                <tr key={g.anahtar}>
                                                    <td className="text-gray-800">{g.ad}</td>
                                                    <td style={{ width: 1 }}>
                                                        <div className='d-flex justify-content-center'>
                                                            <label className='form-check form-check-sm form-check-custom form-check-solid'>
                                                                <input onChange={() => gunuDegistir(g.anahtar, 'calisir', !gunler[g.anahtar].calisir)} className='form-check-input' type='checkbox' checked={gunler[g.anahtar].calisir} />
                                                            </label>
                                                        </div>
                                                    </td>
                                                    <td>{saatGirdisi(g.anahtar, 'baslangic')}</td>
                                                    <td>{saatGirdisi(g.anahtar, 'bitis')}</td>
                                                    <td>{saatGirdisi(g.anahtar, 'araBaslangic')}</td>
                                                    <td>{saatGirdisi(g.anahtar, 'araBitis')}</td>
                                                    <td>{dakikaGirdisi(g.anahtar, 'bildirimDk')}</td>
                                                    <td>{dakikaGirdisi(g.anahtar, 'hatirlatmaDk')}</td>
                                                </tr>
                                            )
                                        }
                                    </tbody>
                                </table>
                            </div>

                            <div className="text-muted fs-7 mt-3">
                                <b>Mesai hatırlatma:</b> mobil kullanıcıya sesli ve yazılı bildirim gider. Örnek: başlangıç 08:00, Bildirim 30, Hatırlatma 10 →
                                07:30, 07:40, 07:50 "Mesainizi başlatmayı unutmayın" (mesai başlatılınca durur); bitişten 30 dk sonra 10 dk arayla en fazla 3 kez
                                "Mesainizi kapatmayı unutmayın" (yalnız mesaisi açık olana, kapatılınca durur). Ek mesai talebi varsa bitiş talebin bitişidir.
                                Bildirim boşsa o gün bildirim gitmez; Hatırlatma boşsa yalnız ilk bildirim gider. Resmi tatilde gönderilmez.
                            </div>
                        </KTCardBody>
                    </KTCard>

                    <div className='containerPart' ref={molaRef}>
                        <KTCard className="ebbe-card mt-10">
                            <KTCardBody>
                                <h4 className="mb-10 text-black">Molalar</h4>

                                {
                                    isOpen && (
                                        <React.Fragment>
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <CustomTextInput labelText="Ad" isLabelRequired placeholder="Örn. Sabah çayı" value={molaAd} setValue={setMolaAd} />
                                                </div>
                                                <div className="col-md-3">
                                                    <label className="form-label required">Başlangıç</label>
                                                    <input type="time" className="form-control form-control-solid" style={{ border: '1px solid #F1F1F4' }} value={molaBaslangic} onChange={(e) => setMolaBaslangic(e.target.value)} />
                                                </div>
                                                <div className="col-md-3">
                                                    <label className="form-label required">Bitiş</label>
                                                    <input type="time" className="form-control form-control-solid" style={{ border: '1px solid #F1F1F4' }} value={molaBitis} onChange={(e) => setMolaBitis(e.target.value)} />
                                                </div>
                                            </div>

                                            <div className="row mt-5">
                                                <div className="col-md-12">
                                                    <label className="form-label required">Günler</label>
                                                    <div className="d-flex flex-row flex-wrap">
                                                        {
                                                            GUNLER.map(g =>
                                                                <label key={g.molaAlani} className='form-check form-check-sm form-check-custom form-check-solid me-10 mb-2'>
                                                                    <input
                                                                        className='form-check-input'
                                                                        type='checkbox'
                                                                        checked={molaGunleriSecim[g.molaAlani]}
                                                                        onChange={() => setMolaGunleriSecim({ ...molaGunleriSecim, [g.molaAlani]: !molaGunleriSecim[g.molaAlani] })}
                                                                    />
                                                                    <span className="form-check-label text-gray-800">{g.ad}</span>
                                                                </label>
                                                            )
                                                        }
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="row mt-5">
                                                <div className="col-md-12">
                                                    <Box alignItems="flex-end">
                                                        <Box onClick={saveItem} className="actionIcon p-2 bg-success" justifyContent="center" flexDirection="row" borderRadius="4px">
                                                            <Box mr="5px" justifyContent="center">
                                                                <i className="text-white fa fa-check" />
                                                            </Box>
                                                            <Box justifyContent="center">
                                                                <span style={{ fontWeight: 600 }} className="text-white">Kaydet</span>
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                </div>
                                            </div>

                                            <Box className='ebbe-hr m-0 mt-5'>&nbsp;</Box>
                                        </React.Fragment>
                                    )
                                }

                                <Box className={isOpen ? "mt-5" : ""} flexDirection="row">
                                    <Box onClick={cancelAdding} className={"actionIcon p-2 " + (!isOpen ? "bg-success" : "")} opacity={"1"} bg={isOpen ? 'orange' : undefined} justifyContent="center" flexDirection="row" borderRadius="4px">
                                        <Box mr="5px" justifyContent="center">
                                            <i className={"text-white fa " + (!isOpen ? 'fa-plus' : 'fa-times')} />
                                        </Box>
                                        <Box justifyContent="center">
                                            <span style={{ fontWeight: 600 }} className="text-white">{!isOpen ? 'Ekle' : 'İptal Et'}</span>
                                        </Box>
                                    </Box>

                                    <Box onClick={seciliSayisi == 1 ? setUpdateItem : undefined} opacity={seciliSayisi == 1 ? "1" : "0.5"} ml="10px" className="actionIcon p-2 bg-primary" justifyContent="center" flexDirection="row" borderRadius="4px">
                                        <Box mr="5px" justifyContent="center">
                                            <i className="text-white fa fa-edit" />
                                        </Box>
                                        <Box justifyContent="center">
                                            <span style={{ fontWeight: 600 }} className="text-white">Güncelle</span>
                                        </Box>
                                    </Box>

                                    <Box onClick={seciliSayisi > 0 ? () => dispatch(storeAreYouSureModalCallback(deleteItems)) : undefined} opacity={seciliSayisi > 0 ? "1" : "0.5"} ml="10px" className="actionIcon p-2 bg-danger" justifyContent="center" flexDirection="row" borderRadius="4px">
                                        <Box mr="5px" justifyContent="center">
                                            <i className="text-white fa fa-trash" />
                                        </Box>
                                        <Box justifyContent="center">
                                            <span style={{ fontWeight: 600 }} className="text-white">Sil</span>
                                        </Box>
                                    </Box>
                                </Box>

                                <div className="row mt-5">
                                    <div className="col-md-12">
                                        <div className='table-responsive'>
                                            <table id='kt_table_users' className='table align-middle table-row-dashed dataTable no-footer'>
                                                <thead>
                                                    <tr className='text-start text-muted fw-bolder text-uppercase'>
                                                        <th style={{ width: 1 }}>
                                                            <div className='d-flex'>
                                                                <label className='form-check form-check-sm form-check-custom form-check-solid'>
                                                                    <input onChange={selectAll} className='form-check-input' type='checkbox' checked={seciliSayisi > 0 && seciliSayisi == molalar.length} />
                                                                </label>
                                                            </div>
                                                        </th>
                                                        <th>Ad</th>
                                                        <th>Başlangıç</th>
                                                        <th>Bitiş</th>
                                                        <th>Günler</th>
                                                    </tr>
                                                </thead>
                                                <tbody className='text-gray-600 fw-bold'>
                                                    {
                                                        molalar.length == 0 && (
                                                            <tr>
                                                                <td className="text-center" colSpan={99}>Kayıt bulunamadı.</td>
                                                            </tr>
                                                        )
                                                    }
                                                    {
                                                        molalar.map(item =>
                                                            <tr key={item.anahtar}>
                                                                <td style={{ width: 1 }}>
                                                                    <div className='d-flex'>
                                                                        <label className='form-check form-check-sm form-check-custom form-check-solid'>
                                                                            <input onChange={() => setItemSelected(item.anahtar)} className='form-check-input' type='checkbox' checked={item.isSelected} />
                                                                        </label>
                                                                    </div>
                                                                </td>
                                                                <td className="text-gray-800">{item.ad}</td>
                                                                <td>{item.baslangic}</td>
                                                                <td>{item.bitis}</td>
                                                                <td>{molaGunleri(item)}</td>
                                                            </tr>
                                                        )
                                                    }
                                                </tbody>
                                            </table>
                                        </div>

                                        <div className="text-muted fs-7">
                                            Molalar normal mesaiden düşülür (kişi o saatte içerideyse); ek mesaiyi etkilemez. Örnek: Sabah çayı 10:00–10:15, Akşam çayı 16:00–16:15.
                                            {' '}Grid'deki değişiklikler sayfanın altındaki Kaydet ile kaydedilir.
                                        </div>
                                    </div>
                                </div>
                            </KTCardBody>
                        </KTCard>
                    </div>

                    <KTCard className="ebbe-card mt-10">
                        <KTCardBody>
                            <h4 className="mb-10 text-black">
                                Ek mesai katsayıları
                                <span className="text-muted fs-7 fw-normal ms-3">Personel puantajında ve müşteri siparişinde aynı</span>
                            </h4>

                            <div className="row">
                                <div className="col-md-3">
                                    {carpanGirdisi('Hafta içi (Pazartesi–Cuma)', carpanHaftaIci, setCarpanHaftaIci)}
                                </div>
                                <div className="col-md-3">
                                    {carpanGirdisi('Cumartesi', carpanCumartesi, setCarpanCumartesi)}
                                </div>
                                <div className="col-md-3">
                                    {carpanGirdisi('Pazar', carpanPazar, setCarpanPazar)}
                                </div>
                                <div className="col-md-3">
                                    {carpanGirdisi('Resmi tatil', carpanResmiTatil, setCarpanResmiTatil, false)}
                                </div>
                            </div>

                            <div className="row mt-5">
                                <div className="col-md-3">
                                    <label className="form-label required">Saatlik ücret böleni (maaş ÷ bölen)</label>
                                    <input
                                        type="number"
                                        step="1"
                                        min="1"
                                        max="400"
                                        className="form-control form-control-solid"
                                        style={{ border: '1px solid #F1F1F4' }}
                                        value={saatlikBolen}
                                        onChange={(e) => setSaatlikBolen(e.target.value)}
                                    />
                                </div>
                                <div className="col-md-9 d-flex align-items-end">
                                    <div className="text-muted fs-7 pb-3">
                                        Personel ücret kaydında saatlik ücret önerisi için kullanılır. Örnek: maaş 30.000 ÷ {sayi(saatlikBolen) || 200} = {(30000 / (sayi(saatlikBolen) || 200)).toLocaleString('tr-TR', { maximumFractionDigits: 2 })} ₺/saat.
                                    </div>
                                </div>
                            </div>

                            <div className="text-muted fs-7 mt-5">
                                Saatler işletme için bir kez girilir ve tüm personele uygulanır. Girilen bitişin üstü ek mesaidir; çalışma günü olmayan günde okutulan sürenin tamamı ek mesaidir. Cuma, hafta içi katsayısını kullanır; resmi tatil boşsa günün katsayısı geçer.
                                {' '}Katsayılar hem personelin ek mesai ücretinde (saatlik ücret × katsayı) hem QR mesai siparişinde (anlaşmadaki "Mesai Detay Saat" birim fiyatı × katsayı) kullanılır; değiştirince ikisi de yeniden hesaplanır. Anlaşmadaki gün çarpanları artık kullanılmaz.
                            </div>
                        </KTCardBody>
                    </KTCard>

                    <KTCard className="ebbe-card mt-10">
                        <KTCardBody>
                            <h4 className="mb-2 text-black">Talep saat aralıkları</h4>
                            <div className="text-muted fs-7 mb-5">
                                Ek mesai talebinde planlamacının seçtiği aralıklar (web ve mobil). Lojistik mesai talebin aralığıyla kesişirse yerleşir; faturalanan, aralığa düşen ek mesaidir.
                            </div>

                            <div className="d-flex flex-wrap gap-3 mb-5">
                                {
                                    talepAraliklari.map(a => {
                                        var c = saatAraligiCoz(a);

                                        return (
                                            <span key={a} className="badge badge-light-primary fs-6 fw-bold py-3 px-4 d-inline-flex align-items-center">
                                                {c ? saatAraligiEtiketi(c.baslangic, c.bitis) : a}
                                                <i
                                                    className="fa fa-times ms-3 actionIcon text-danger"
                                                    role="button"
                                                    title="Aralığı sil"
                                                    aria-label={(c ? saatAraligiEtiketi(c.baslangic, c.bitis) : a) + ' aralığını sil'}
                                                    onClick={() => aralikSil(a)}
                                                />
                                            </span>
                                        )
                                    })
                                }
                            </div>

                            <div className="row align-items-end">
                                <div className="col-md-3">
                                    <label className="form-label">Başlangıç</label>
                                    <input type="time" className="form-control form-control-solid" style={{ border: '1px solid #F1F1F4' }} value={yeniAralikBaslangic} onChange={(e) => setYeniAralikBaslangic(e.target.value)} />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">Bitiş</label>
                                    <input type="time" className="form-control form-control-solid" style={{ border: '1px solid #F1F1F4' }} value={yeniAralikBitis} onChange={(e) => setYeniAralikBitis(e.target.value)} />
                                </div>
                                <div className="col-md-3">
                                    <button type="button" className="btn btn-sm btn-success mb-1" onClick={aralikEkle}>
                                        <i className="fa fa-plus me-1" />
                                        Aralık ekle
                                    </button>
                                </div>
                            </div>

                            <div className="text-muted fs-7 mt-5">
                                En az bir aralık olmalı; bitiş başlangıçtan sonra (gece yarısını geçen aralık girilmez). Örnek: 08:00–18:00 hafta sonu / resmi tatil, 18:00–22:00 akşam.
                                {' '}Değişiklikler sayfanın altındaki Kaydet ile kaydedilir; mevcut talepler etkilenmez.
                                {araliklarVarsayilan && <span className="text-warning"> Liste henüz kaydedilmemiş; önerilen varsayılan aralıklar gösteriliyor.</span>}
                            </div>
                        </KTCardBody>
                    </KTCard>

                    <KTCard className="ebbe-card mt-10">
                        <KTCardBody>
                            <div className="d-flex flex-row justify-content-between align-items-center">
                                <div className="text-muted fs-7">
                                    {(updatedDate || updatedUser) ? `Son güncelleme: ${updatedDate ?? ''} · ${updatedUser ?? ''}` : ''}
                                </div>
                                <div>
                                    <button className="btn btn-primary btn-sm" onClick={kaydet}>Kaydet</button>
                                </div>
                            </div>
                        </KTCardBody>
                    </KTCard>
                </Content>
            </Box>
        </React.Fragment>
    )
}

export default CalismaSaati;
